import React, { useEffect, useMemo, useState } from 'react';
import { Printer, AlertCircle, Loader2, SlidersHorizontal, X, Save } from 'lucide-react';
import apiClient from '../../config/api.config';
import { useGetOptionsQuery } from '../../services/polesOptionsApi';

interface Creneau {
  classe: string; libelle_matiere: string; formateur_nom: string;
  jour: string; jour_label: string; heure_debut: string; heure_fin: string;
  salle: string; semestre: number | null;
}
const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const JOUR_LABEL: Record<string, string> = {
  lundi: 'LUNDI', mardi: 'MARDI', mercredi: 'MERCREDI', jeudi: 'JEUDI', vendredi: 'VENDREDI', samedi: 'SAMEDI',
};
const NIVEAUX = ['L1', 'L2', 'L3', 'M1', 'M2'];
const cycleLabel = (n: string) =>
  n.startsWith('L') ? `LICENCE ${n[1]}` : n.startsWith('M') ? `MASTER ${n[1]}` : n;

// Classification best-effort du type de module -> couleur (ajustable).
const TYPE_COULEUR: Record<string, { bg: string; label: string }> = {
  fondamental: { bg: '#d9ead3', label: 'Module fondamental' },
  transversal: { bg: '#cfe2f3', label: 'Module transversal' },
  optionnel: { bg: '#ead1dc', label: 'Module optionnel / professionnel' },
};
type TypeMod = 'fondamental' | 'transversal' | 'optionnel';
const classer = (nom: string): TypeMod => {
  const n = (nom || '').toLowerCase();
  if (/anglais|communication|informatique|m[ée]thodolog|insertion|tef|entrepreneur|leadership|expression|bureautique|r[ée]daction/.test(n)) return 'transversal';
  if (/stage|projet|s[ée]minaire|soutenance|professionnel|optionnel|sp[ée]cialit/.test(n)) return 'optionnel';
  return 'fondamental';
};
// doit correspondre à _norm() côté serveur
const normCle = (s: string) =>
  (s || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

interface ModuleType { nom: string; cle: string; type: TypeMod; defini: boolean; }

const ImpressionEDT: React.FC = () => {
  const { data: options } = useGetOptionsQuery({ actif: true } as any);
  const [annees, setAnnees] = useState<{ libelle: string; active: boolean }[]>([]);
  const [annee, setAnnee] = useState('');
  const [niveau, setNiveau] = useState('L1');
  const [optionNom, setOptionNom] = useState('');
  const [semestre, setSemestre] = useState('1');
  const [taille, setTaille] = useState(11);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // mapping type de module (cle normalisée -> type)
  const [typeMap, setTypeMap] = useState<Record<string, TypeMod>>({});
  const [showTypes, setShowTypes] = useState(false);
  const [modules, setModules] = useState<ModuleType[]>([]);
  const [modLoading, setModLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get('/migration-reference/annees/').then((r) => {
      const l = r.data.annees || [];
      setAnnees(l);
      const act = l.find((a: any) => a.active);
      if (act) setAnnee(act.libelle);
    }).catch(() => {});
    chargerTypes();
  }, []);

  const chargerTypes = async () => {
    try {
      const r = await apiClient.get('/vie-scolaire/types-modules/');
      const list: ModuleType[] = r.data.modules || [];
      setModules(list);
      const m: Record<string, TypeMod> = {};
      list.filter((x) => x.defini).forEach((x) => { m[x.cle] = x.type; });
      setTypeMap(m);
    } catch { /* heuristique en repli */ }
  };

  // type effectif d'un module : mapping enregistré sinon heuristique
  const typeDe = (nom: string): TypeMod => typeMap[normCle(nom)] || classer(nom);

  const ouvrirModal = async () => {
    setShowTypes(true); setModLoading(true);
    await chargerTypes();
    setModLoading(false);
  };

  const majType = (cle: string, type: TypeMod) =>
    setModules((prev) => prev.map((m) => (m.cle === cle ? { ...m, type, defini: true } : m)));

  const enregistrerTypes = async () => {
    setSaving(true);
    try {
      await apiClient.post('/vie-scolaire/types-modules/', {
        items: modules.map((m) => ({ nom: m.nom, type: m.type })),
      });
      const m: Record<string, TypeMod> = {};
      modules.forEach((x) => { m[x.cle] = x.type; });
      setTypeMap(m);
      setShowTypes(false);
    } catch {
      setMsg('Enregistrement des types impossible.');
    } finally { setSaving(false); }
  };

  const optionSel = useMemo(() => (options || []).find((o: any) => o.nom === optionNom), [options, optionNom]);

  const imprimer = async () => {
    if (!optionSel) { setMsg('Choisissez une option.'); return; }
    const code = `${niveau}${optionSel.code}`;
    setLoading(true); setMsg(null);
    try {
      const [edtR, instR] = await Promise.all([
        apiClient.get('/vie-scolaire/edt/', { params: { classe: code }, headers: annee ? { 'X-Annee-Academique': annee } : {} }),
        apiClient.get('/parametres/info-institut/').catch(() => ({ data: {} })),
      ]);
      let crs: Creneau[] = (edtR.data.results || []);
      const semNum = parseInt(semestre, 10);
      crs = crs.filter((c) => !c.semestre || c.semestre === semNum);
      if (crs.length === 0) { setMsg(`Aucun créneau pour ${code} (semestre ${semestre}).`); setLoading(false); return; }
      ouvrirImpression(crs, instR.data || {});
    } catch {
      setMsg('Chargement de l’EDT impossible.');
    } finally { setLoading(false); }
  };

  const ouvrirImpression = (crs: Creneau[], inst: any) => {
    const esc = (s: any) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // créneaux horaires distincts, triés
    const slots = Array.from(new Set(crs.map((c) => `${c.heure_debut}-${c.heure_fin}`)))
      .sort((a, b) => a.localeCompare(b));
    // index des créneaux par (jour, slot)
    const idx: Record<string, Creneau[]> = {};
    crs.forEach((c) => {
      const k = `${c.jour}|${c.heure_debut}-${c.heure_fin}`;
      (idx[k] = idx[k] || []).push(c);
    });
    const fmtH = (h: string) => h.slice(0, 5).replace(':', 'h');
    const estMatin = (slot: string) => parseInt(slot.slice(0, 2), 10) < 13;

    let corps = '';
    let pauseInseree = false;
    slots.forEach((slot) => {
      if (!pauseInseree && !estMatin(slot)) {
        corps += `<tr><td class="hslot">PAUSE</td><td class="pause" colspan="${JOURS.length}">Pause</td></tr>`;
        pauseInseree = true;
      }
      const [hd, hf] = slot.split('-');
      corps += `<tr><td class="hslot">${fmtH(hd)}<br>–<br>${fmtH(hf)}</td>`;
      JOURS.forEach((j) => {
        const items = idx[`${j}|${slot}`] || [];
        if (items.length === 0) { corps += '<td></td>'; return; }
        const cells = items.map((c) => {
          const t = typeDe(c.libelle_matiere);
          const bg = TYPE_COULEUR[t].bg;
          const prof = c.formateur_nom ? `(${esc(c.formateur_nom)})` : '(xxxxxxxxxx)';
          const lieu = c.salle ? esc(c.salle) : '';
          return `<div class="cell" style="background:${bg}">`
            + `<div class="mat">${esc(c.libelle_matiere)}</div>`
            + `<div class="prof">${prof}</div>`
            + (lieu ? `<div class="salle">${lieu}</div>` : '') + `</div>`;
        }).join('');
        corps += `<td class="occ">${cells}</td>`;
      });
      corps += '</tr>';
    });

    const annee_lib = annee || inst.annee || '';
    const ville = inst.ville || 'Pointe-Noire';
    const dept = niveau.startsWith('M') ? 'DÉPARTEMENT DE MASTER' : 'DÉPARTEMENT DE LICENCE';
    const tel = inst.telephone || '';
    const email = inst.email || '';
    const site = inst.site_web || 'www.institut-international-2i.com';
    const agr = inst.numero_agrement || "Agrément N° 0196/MES – CAB.DG-DAAC du 21/04/2021";
    const adresse = inst.adresse_rue || '';
    const legende = Object.values(TYPE_COULEUR).map((t) =>
      `<span class="lg"><span class="sw" style="background:${t.bg}"></span>${t.label}</span>`).join('');

    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>EDT ${esc(niveau)} ${esc(optionSel!.code)}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        * { box-sizing: border-box; }
        body { font-family: "Trebuchet MS", "Segoe UI", Arial, sans-serif; color: #1a1a1a; margin: 0; font-size: ${taille}px; }
        .head { display: flex; align-items: center; gap: 14px; }
        .head img { width: 70px; height: 70px; object-fit: contain; }
        .head .daac { font-size: 0.9em; line-height: 1.4; text-align: center; flex: 1; }
        .head .daac b { color: #0b3d91; }
        .titre { border: 1px solid #888; background: #eee; text-align: center; padding: 5px 16px; border-radius: 4px; font-weight: bold; }
        .titre .s { font-size: 0.85em; font-weight: normal; }
        .head .an { font-size: 0.9em; text-align: right; min-width: 130px; }
        .ligne { display: flex; justify-content: space-between; align-items: baseline; margin: 10px 2px 8px; }
        .ligne .opt { color: #0b3d91; font-weight: bold; font-size: 1.05em; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #555; padding: 3px; text-align: center; vertical-align: middle; }
        thead th { background: #f3f3f3; font-size: 0.9em; text-transform: uppercase; }
        td.hslot { width: 62px; font-weight: bold; font-size: 0.85em; background: #fafafa; }
        td.occ { padding: 2px; }
        td.pause { background: #f3f3f3; font-style: italic; color: #555; }
        .cell { border-radius: 3px; padding: 4px 3px; }
        .cell .mat { font-weight: bold; font-size: 0.92em; }
        .cell .prof { font-size: 0.82em; font-style: italic; color: #333; }
        .cell .salle { font-size: 0.78em; font-style: italic; color: #444; margin-top: 1px; }
        .legende { display: flex; gap: 22px; margin-top: 12px; font-size: 0.85em; flex-wrap: wrap; }
        .lg { display: inline-flex; align-items: center; gap: 6px; }
        .sw { width: 22px; height: 13px; border: 1px solid #999; display: inline-block; border-radius: 2px; }
        .bas { display: flex; justify-content: space-between; margin-top: 10px; font-size: 0.85em; }
        .nb { color: #333; }
        .sign { text-align: center; }
        .foot { margin-top: 14px; border-top: 2px solid #9db8d2; padding-top: 5px; text-align: center; font-size: 0.75em; color: #777; }
        .foot .agr { color: #b06; }
      </style></head><body>
      <div class="head">
        <img src="/logo.png" alt="logo">
        <div class="daac">
          <b>INSTITUT INTERNATIONAL 2i</b><br>
          <b>DIRECTION DES AFFAIRES ACADÉMIQUES</b><br>${dept}
        </div>
        <div class="titre">EMPLOI DU TEMPS<br><span class="s">(Semestre ${esc(semestre)})</span></div>
        <div class="an">Année académique<br><b>${esc(annee_lib)}</b></div>
      </div>
      <div class="ligne">
        <div><b>${cycleLabel(niveau)}</b> &nbsp;&bull;&nbsp; ${esc(optionSel!.pole_nom || '')}</div>
        <div class="opt">${esc(optionSel!.nom)} (${esc(optionSel!.code)})</div>
        <div>LIEU : ${esc(ville)}</div>
      </div>
      <table>
        <thead><tr><th class="hslot">Horaire</th>${JOURS.map((j) => `<th>${JOUR_LABEL[j]}</th>`).join('')}</tr></thead>
        <tbody>${corps}</tbody>
      </table>
      <div class="legende">${legende}</div>
      <div class="bas">
        <div class="nb"><b>NB :</b> Cet emploi du temps est modulaire, il peut être modifié pour une meilleure adaptation.</div>
        <div class="sign">Fait à ${esc(ville)}, le ______________<br><br><b>Le Directeur,</b></div>
      </div>
      <div class="foot">
        <div class="agr">${esc(agr)}</div>
        <div>CAMPUS DE ${esc((ville || '').toUpperCase())}${adresse ? ' – ' + esc(adresse) : ''}</div>
        <div>${tel ? 'Tél. : ' + esc(tel) + ' – ' : ''}${email ? 'E-mail : ' + esc(email) + ' – ' : ''}Site : ${esc(site)}</div>
      </div>
      <script>window.addEventListener('load', function(){ setTimeout(function(){ try{ window.focus(); window.print(); }catch(e){} }, 300); });</script>
      </body></html>`;
    const w = window.open('', '_blank');
    if (!w) { setMsg('Le navigateur a bloqué l’ouverture. Autorisez les popups.'); return; }
    w.document.open(); w.document.write(html); w.document.close();
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Printer size={26} /> Impression des emplois du temps
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Génère l'EDT imprimable d'une classe (par niveau et option), coloré par type de module.
        </p>
      </div>

      {msg && (
        <div className="mb-4 p-3 rounded-lg flex items-center gap-2 text-sm bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          <AlertCircle size={18} /><span>{msg}</span>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Année académique</label>
            <select value={annee} onChange={(e) => setAnnee(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              {annees.map((a) => <option key={a.libelle} value={a.libelle}>{a.libelle}{a.active ? ' (active)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Semestre</label>
            <select value={semestre} onChange={(e) => setSemestre(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="1">Semestre 1</option>
              <option value="2">Semestre 2</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Niveau</label>
            <select value={niveau} onChange={(e) => setNiveau(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              {NIVEAUX.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Option</label>
            <select value={optionNom} onChange={(e) => setOptionNom(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
              <option value="">-- Choisir --</option>
              {(options || []).slice().sort((a: any, b: any) => a.nom.localeCompare(b.nom)).map((o: any) => (
                <option key={o.id} value={o.nom}>[{o.pole_nom?.slice(0, 4) || '?'}] {o.code} — {o.nom}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Taille de police : <b>{taille}px</b></label>
          <input type="range" min={8} max={18} value={taille} onChange={(e) => setTaille(Number(e.target.value))} className="w-full accent-blue-600" />
        </div>
        <div className="flex justify-between items-center">
          <button onClick={ouvrirModal}
            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg">
            <SlidersHorizontal size={16} /> Types de modules
          </button>
          <button onClick={imprimer} disabled={loading || !optionNom}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Printer size={16} />}
            Imprimer l'EDT
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
        {Object.entries(TYPE_COULEUR).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="w-4 h-3 rounded-sm border border-gray-300" style={{ background: v.bg }} />{v.label}
          </span>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-2">
        Les couleurs sont déduites du nom du module, mais vous pouvez les fixer précisément module par module
        via « Types de modules ».
      </p>

      {showTypes && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <h2 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <SlidersHorizontal size={18} /> Types de modules
              </h2>
              <button onClick={() => setShowTypes(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <p className="text-xs text-gray-500 mb-3">
                Affectez à chaque module sa couleur. Les modules issus des emplois du temps sont listés ci-dessous ;
                une valeur déduite est proposée par défaut.
              </p>
              {modLoading ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
                  <Loader2 size={16} className="animate-spin" /> Chargement…
                </div>
              ) : modules.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">Aucun module trouvé dans les emplois du temps.</p>
              ) : (
                <div className="space-y-1.5">
                  {modules.map((m) => (
                    <div key={m.cle} className="flex items-center gap-3 py-1.5 border-b border-gray-100 dark:border-gray-700/50">
                      <span className="flex-1 text-sm text-gray-800 dark:text-gray-100 truncate" title={m.nom}>
                        {m.nom}{!m.defini && <span className="text-gray-400 text-xs"> (déduit)</span>}
                      </span>
                      <div className="flex gap-1">
                        {(Object.keys(TYPE_COULEUR) as TypeMod[]).map((t) => (
                          <button key={t} onClick={() => majType(m.cle, t)}
                            title={TYPE_COULEUR[t].label}
                            className={`w-7 h-7 rounded border-2 ${m.type === t ? 'border-gray-800 dark:border-white' : 'border-transparent'}`}
                            style={{ background: TYPE_COULEUR[t].bg }} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setShowTypes(false)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                Annuler
              </button>
              <button onClick={enregistrerTypes} disabled={saving || modules.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpressionEDT;
